import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, MessageCircle } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FloatingButtons from '../components/FloatingButtons';
import { companyInfo } from '../data/mock';

const ContactPage = () => {
  const contactInfo = [
    {
      icon: Phone,
      title: 'Phone Numbers',
      details: companyInfo.phones
    },
    {
      icon: Mail,
      title: 'Email Address',
      details: [companyInfo.email]
    },
    {
      icon: MapPin,
      title: 'Our Location',
      details: [companyInfo.address]
    },
    {
      icon: Clock,
      title: 'Working Hours',
      details: [companyInfo.timings.weekdays, companyInfo.timings.sunday]
    }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-[#007897] to-[#006680]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center text-white">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Contact Us</h1>
            <p className="text-blue-100 max-w-2xl mx-auto">
              Have questions? We are here to help.
            </p>
            <div className="flex items-center justify-center gap-2 mt-6 text-sm">
              <Link to="/" className="text-blue-200 hover:text-white">Home</Link>
              <span className="text-blue-300">/</span>
              <span>Contact Us</span>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Get in Touch</h2>
              <p className="text-gray-600 mb-8">
                Our team is ready to assist you with all your diagnostic needs.
              </p>

              <div className="space-y-6">
                {contactInfo.map((item, index) => (
                  <div key={`contact-item-${index}`} className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                    <div className="w-12 h-12 bg-[#007897]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-6 h-6 text-[#007897]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">{item.title}</h3>
                      {item.details.map((detail, i) => {
                        // Determine if clickable and what type
                        const isPhone = item.title === 'Phone Numbers';
                        const isEmail = item.title === 'Email Address';
                        const isAddress = item.title === 'Our Location';
                        
                        if (isPhone) {
                          return (
                            <a 
                              key={`phone-${detail}`} 
                              href={`tel:${detail}`}
                              className="block text-gray-600 text-sm hover:text-[#007897] hover:underline transition-colors"
                            >
                              {detail}
                            </a>
                          );
                        } else if (isEmail) {
                          return (
                            <a 
                              key={`email-${detail}`} 
                              href={`mailto:${detail}`}
                              className="block text-gray-600 text-sm hover:text-[#007897] hover:underline transition-colors"
                            >
                              {detail}
                            </a>
                          );
                        } else if (isAddress) {
                          return (
                            <a 
                              key={`address-${detail.substring(0, 20)}`} 
                              href={`https://maps.google.com/?q=${encodeURIComponent(detail)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block text-gray-600 text-sm hover:text-[#007897] hover:underline transition-colors"
                            >
                              {detail}
                            </a>
                          );
                        } else {
                          return (
                            <p key={`detail-${detail.substring(0, 20)}`} className="text-gray-600 text-sm">{detail}</p>
                          );
                        }
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Contact Info Display */}
            <div>
              <div className="bg-gray-50 rounded-2xl p-8 flex flex-col justify-center">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Information</h2>
                <p className="text-gray-600 mb-8">
                  Our team is available during working hours to assist you.
                </p>

                <div className="space-y-4">
                  {/* Phone Info - Clickable */}
                  <a 
                    href={`tel:${companyInfo.phones[0]}`}
                    className="flex items-center justify-center gap-3 w-full p-4 bg-[#007897] text-white rounded-xl hover:bg-[#006680] transition-colors cursor-pointer"
                  >
                    <Phone size={24} />
                    <span className="font-semibold">Phone: {companyInfo.phones[0]}</span>
                  </a>

                  {/* WhatsApp Info - Clickable */}
                  <a 
                    href={`https://wa.me/${companyInfo.whatsapp.replace(/[^0-9]/g, '')}?text=${encodeURIComponent('Hello, I would like to inquire about your diagnostic services.')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-3 w-full p-4 bg-green-500 text-white rounded-xl hover:bg-green-600 transition-colors cursor-pointer"
                  >
                    <MessageCircle size={24} />
                    <span className="font-semibold">WhatsApp: {companyInfo.whatsapp}</span>
                  </a>

                  {/* Secondary Phone - Clickable */}
                  <a 
                    href={`tel:${companyInfo.phones[3]}`}
                    className="flex items-center justify-center gap-3 w-full p-4 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 transition-colors cursor-pointer"
                  >
                    <Phone size={20} />
                    <span>Mobile: {companyInfo.phones[3]}</span>
                  </a>
                </div>

                <div className="mt-8 pt-6 border-t border-gray-200">
                  <p className="text-gray-600 text-sm text-center">
                    {companyInfo.timings.weekdays}<br />
                    {companyInfo.timings.sunday}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="bg-white rounded-2xl overflow-hidden shadow-lg">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3498.7654321098!2d77.1223689!3d28.6996683!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390d017b571a1bb3%3A0x7ab3596c97f2d686!2sNanda%20Diagnostics!5e0!3m2!1sen!2sin!4v1704067200000!5m2!1sen!2sin"
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Nanda Diagnostics Location"
            />
            <div className="p-4 bg-white border-t flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-[#007897]" />
                <div>
                  <p className="font-semibold text-gray-900">Nanda Diagnostics</p>
                  <p className="text-sm text-gray-600">C-566, Outer Ring Road, Saraswati Vihar, Pitam Pura, Delhi – 110034</p>
                </div>
              </div>
              <a 
                href="https://maps.app.goo.gl/s7P5qjNoimHRueF37"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#007897] text-white px-4 py-2 rounded-lg hover:bg-[#006680] transition-colors text-sm font-medium"
              >
                <MapPin className="w-4 h-4" />
                Open in Google Maps
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingButtons />
    </div>
  );
};

export default ContactPage;
